/* ==========================================================================
   MINI by BLITZBOX - the booking panel on a rental product page.

   The page is built by sections/mini-booking.liquid; this script drives it:
   the calendar, the rental period and its price, which delivery methods are
   possible for the chosen date, the add-ons, the running total, and finally
   the hand-over to the cart and checkout.

   Dates decide the price. Picking them works like booking a return flight:
   two boxes, "Første dag" and "Siste dag", and the highlighted one is what the
   next click in the calendar sets. The period and price follow from the days. The client's rules: the booth arrives
   before Friday and goes back on the first weekday after the event, so a
   rental that touches a Friday, Saturday or Sunday always covers the whole
   weekend. Friday-Sunday is the weekend price ("Helg"); Thursday on into the
   weekend is the long weekend ("Langhelg", Thursday-Sunday). Any other span
   is priced by its number of days.

   Prices are never invented here. Every amount is the price of a real variant
   or product in the shop, read from the page, so what the panel shows is what
   the checkout charges.

   How a rental period becomes a variant: each variant of the rental product is
   one period. Its SKU ends in a code - "1D", "2D", "3-4D", "8-14D" for a number
   of days, "HELG" for Friday to Sunday, "LANGHELG" for Thursday to Sunday.
   With no SKU the variant's name is read instead ("3-4 dager", "1 uke",
   "Helg", "Langhelg"). Change a price in the admin and the panel follows.
   ========================================================================== */
(() => {
  if (customElements.get('mini-booking')) return;

  const MONTHS = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
  const MONTHS_SHORT = ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'];
  const DAYS_SHORT = ['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'];
  // Tilpass comes before Levering: an add-on such as Print decides which deliveries are possible.
  const STEPS = ['date', 'custom', 'delivery', 'summary'];
  const DAY_MS = 86400000;
  const ICONS = {
    truck: '<svg class="mini-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 6.5h11v10h-11zM13.5 10h4l3 3.2v3.3h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>',
    palette: '<svg class="mini-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3a9 9 0 1 0 0 18c1.4 0 2-.9 2-1.8 0-1.6-1.2-1.7-1.2-3 0-1 .8-1.7 1.8-1.7H17a4 4 0 0 0 4-4C21 6.4 17 3 12 3z"/><circle cx="7.6" cy="11.6" r="1"/><circle cx="10" cy="7.6" r="1"/><circle cx="14.6" cy="7.4" r="1"/></svg>',
    invoice: '<svg class="mini-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2.8h8.5L19 7.3v13.9H6z"/><path d="M14.5 2.8v4.5H19M9 11.5h7M9 14.8h7M9 18h4"/></svg>',
    camera: '<svg class="mini-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h3l1.5-2.5h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13.2" r="3.4"/></svg>',
  };

  const pad = (n) => (n < 10 ? '0' : '') + n;
  const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const fromIso = (s) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim());
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  };
  const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  const daysBetween = (a, b) => Math.round((b - a) / DAY_MS) + 1;
  const weekday = (d) => (d.getDay() + 6) % 7; // 0 = Monday ... 6 = Sunday
  const kr = (cents) => `${Math.round(cents / 100).toLocaleString('nb-NO')} kr`;
  const shortDate = (d) => `${DAYS_SHORT[weekday(d)]} ${d.getDate()}. ${MONTHS_SHORT[d.getMonth()]}`;
  const dayCount = (n) => (n === 1 ? '1 dag' : `${n} dager`);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /** "fre 13. - søn 15. nov. 2026", or one date when the rental is a single day. */
  function rangeLabel(a, b) {
    if (toIso(a) === toIso(b)) return `${shortDate(a)} ${a.getFullYear()}`;
    const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
    const first = sameMonth ? `${DAYS_SHORT[weekday(a)]} ${a.getDate()}.` : shortDate(a);
    return `${first} – ${shortDate(b)} ${b.getFullYear()}`;
  }

  /** Read one variant as a rental period. Returns null when it is not one. */
  function readTier(variant) {
    const tier = { id: variant.id, price: variant.price, title: variant.title, kind: 'days', min: 0, max: 0 };
    const sku = String(variant.sku || '').toUpperCase();
    const name = String(variant.title || '').toLowerCase();
    let m;
    if (/LANGHELG$/.test(sku)) tier.kind = 'longweekend';
    else if (/(HELG|WEEKEND)$/.test(sku)) tier.kind = 'weekend';
    else if ((m = /(\d+)(?:-(\d+))?D$/.exec(sku))) { tier.min = +m[1]; tier.max = +(m[2] || m[1]); }
    else if (/langhelg/.test(name)) tier.kind = 'longweekend';
    else if (/helg/.test(name)) tier.kind = 'weekend';
    else if ((m = /(\d+)\s*[–-]\s*(\d+)\s*(dag|uke)/.exec(name))) { const f = m[3] === 'uke' ? 7 : 1; tier.min = +m[1] * f; tier.max = +m[2] * f; }
    else if ((m = /(\d+)\s*(dag|uke)/.exec(name))) { const f = m[2] === 'uke' ? 7 : 1; tier.min = tier.max = +m[1] * f; }
    else return null;
    return tier;
  }

  const BUNDLES = { weekend: 'Helg (fredag–søndag)', longweekend: 'Langhelg (torsdag–søndag)' };
  /** What the customer sees as the period: the bundle's name, or the number of days. */
  const periodName = (tier, n) => BUNDLES[tier.kind] || dayCount(n);

  /** "2026-12-24, 2026-12-31..2027-01-01" (commas or new lines) as a set of ISO dates. */
  function readBlocked(text) {
    const set = new Set();
    String(text || '').split(/[\n,;]+/).forEach((part) => {
      const [a, b] = part.split('..').map((s) => fromIso(s));
      if (!a) return;
      for (let d = a, end = b || a, guard = 0; d <= end && guard < 400; d = addDays(d, 1), guard++) set.add(toIso(d));
    });
    return set;
  }

  /** "0001-1299, 1300-1399" as a test for a four-digit postcode. */
  function postcodeTest(ranges) {
    const list = String(ranges || '').split(/[\s,;]+/).filter(Boolean).map((r) => r.split('-').map((n) => parseInt(n, 10)));
    return (code) => {
      if (!/^\d{4}$/.test(code)) return false;
      if (!list.length) return true;
      const n = parseInt(code, 10);
      return list.some(([a, b]) => n >= a && n <= (b === undefined ? a : b));
    };
  }

  /* ---------- Posten's working days ---------- */
  /** Easter Sunday (Gregorian calendar, the "anonymous" algorithm). */
  function easter(y) {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    return new Date(y, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1);
  }
  const holidays = {};
  /**
   * Days Posten does not deliver, besides Saturday and Sunday: Norway's public
   * holidays (Maundy Thursday to Easter Monday, Ascension, Whit Monday, 1 and 17
   * May, Christmas, New Year), plus Christmas Eve and New Year's Eve.
   */
  function postHolidays(y) {
    if (!holidays[y]) {
      const e = easter(y);
      holidays[y] = new Set([
        new Date(y, 0, 1), new Date(y, 4, 1), new Date(y, 4, 17), new Date(y, 11, 24), new Date(y, 11, 25), new Date(y, 11, 26), new Date(y, 11, 31),
        addDays(e, -3), addDays(e, -2), e, addDays(e, 1), addDays(e, 39), addDays(e, 49), addDays(e, 50),
      ].map(toIso));
    }
    return holidays[y];
  }
  const isPostDay = (d) => weekday(d) < 5 && !postHolidays(d.getFullYear()).has(toIso(d));
  const nextPostDay = (d) => { let n = addDays(d, 1); while (!isPostDay(n)) n = addDays(n, 1); return n; };

  /** Today's date and hour in Norway, whatever the visitor's own clock says. */
  function osloNow() {
    try {
      const p = {};
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Oslo', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23' })
        .formatToParts(new Date()).forEach((x) => { p[x.type] = x.value; });
      return { date: new Date(+p.year, +p.month - 1, +p.day), hour: +p.hour };
    } catch (e) {
      const now = new Date();
      return { date: new Date(now.getFullYear(), now.getMonth(), now.getDate()), hour: now.getHours() };
    }
  }

  class MiniBooking extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      const d = this.dataset;
      this.handle = d.handle;
      this.productTitle = d.title;
      this.units = Math.max(1, parseInt(d.units, 10) || 1);
      this.bufferBefore = Math.max(0, parseInt(d.bufferBefore, 10) || 0);
      this.bufferAfter = Math.max(0, parseInt(d.bufferAfter, 10) || 0);
      this.occasionRequired = d.occasionRequired === 'true';
      this.inOslo = postcodeTest(d.osloPostcodes);
      this.blocked = readBlocked(this.querySelector('[data-blocked]')?.textContent);
      // Booked dates from the shop's MINI-booking entries, each { from, to }.
      try { this.bookings = JSON.parse(this.querySelector('[data-bookings]')?.textContent || '[]'); } catch (e) { this.bookings = []; }
      this.serviceBusy = {};
      this.applyBookings();

      const variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.tiers = variants.map(readTier).filter(Boolean);
      this.dayTiers = this.tiers.filter((t) => t.kind === 'days').sort((a, b) => a.min - b.min);
      this.weekendTier = this.tiers.find((t) => t.kind === 'weekend') || null;
      this.longTier = this.tiers.find((t) => t.kind === 'longweekend') || null;
      this.maxDays = this.dayTiers.reduce((n, t) => Math.max(n, t.max), 1);

      const now = new Date();
      this.today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      this.minDate = addDays(this.today, Math.max(0, parseInt(d.leadDays, 10) || 0));
      this.maxDate = new Date(this.today.getFullYear(), this.today.getMonth() + (parseInt(d.maxMonths, 10) || 18), this.today.getDate());

      this.storageKey = `mini-booking:${this.handle}`;
      this.state = Object.assign(
        { start: null, end: null, delivery: '', postcode: '', logo: '', occasion: '', eventName: '', addons: [], customer: 'privat', orgnr: '', company: '', payment: 'now', invoiceFormat: 'E-post', invoiceEmail: '', invoiceRef: '', open: 'date', done: [] },
        this.restore()
      );
      const start = fromIso(this.state.start);
      const end = fromIso(this.state.end) || start;
      if (!start || start < this.minDate || end > this.maxDate || end < start) Object.assign(this.state, { start: null, end: null, open: 'date', done: [] });
      else this.state.end = toIso(end);
      // True between the first click (first day) and the second (last day).
      this.picking = false;
      // Which box the next click in the calendar fills: the first day or the last.
      this.active = this.state.start ? 'end' : 'start';
      this.view = this.start ? new Date(start.getFullYear(), start.getMonth(), 1) : new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);

      this.el = {
        cal: this.querySelector('[data-cal]'),
        price: this.querySelector('[data-price]'),
        sum: this.querySelector('[data-sum]'),
        submit: this.querySelector('[data-submit]'),
        zoom: this.querySelector('[data-zoom]'),
        sticky: document.querySelector(`[data-mb-sticky="${this.dataset.section}"]`),
      };
      this.querySelectorAll('[data-kr]').forEach((n) => { n.textContent = (n.dataset.krPrefix || '') + kr(+n.dataset.kr); });

      this.applyStateToInputs();
      this.bind();
      this.render();
      this.loadAvailability(d.availabilityUrl);

      // Back from the checkout: the button must not stay in its waiting state.
      const submitLabel = this.el.submit ? this.el.submit.textContent : '';
      window.addEventListener('pageshow', (e) => {
        if (e.persisted && this.el.submit) { this.el.submit.disabled = false; this.el.submit.textContent = submitLabel; }
      });
    }

    /* ---------- state ---------- */
    restore() {
      try { return JSON.parse(sessionStorage.getItem(this.storageKey) || 'null') || {}; } catch (e) { return {}; }
    }
    save() {
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch (e) { /* private window: carry on without */ }
    }

    get start() { return fromIso(this.state.start); }

    /**
     * The days a choice really covers. A start on Saturday or Sunday moves back
     * to that Friday; an end on Friday or Saturday moves on to that Sunday.
     */
    normalize(a, b) {
      let s = a;
      let e = b || a;
      if (weekday(s) >= 5) s = addDays(s, 4 - weekday(s));
      if (weekday(e) === 4 || weekday(e) === 5) e = addDays(e, 6 - weekday(e));
      return [s, e];
    }

    /** First and last rental day. */
    range() {
      const s = this.start;
      return s ? [s, fromIso(this.state.end) || s] : null;
    }

    /** The period, and so the price, for a rental from a to b. */
    tierFor(a, b) {
      const n = daysBetween(a, b);
      if (this.weekendTier && n === 3 && weekday(a) === 4) return this.weekendTier;
      if (this.longTier && n === 4 && weekday(a) === 3) return this.longTier;
      return this.dayTiers.find((t) => n >= t.min && n <= t.max) || null;
    }

    tier() {
      const r = this.range();
      return r ? this.tierFor(r[0], r[1]) : null;
    }

    /* ---------- availability ---------- */
    /**
     * How many booths are away on each date. A booking keeps its booth away
     * from bufferBefore days before its first day to bufferAfter days after
     * its last, for sending out and getting back.
     */
    applyBookings() {
      this.busy = Object.assign({}, this.serviceBusy);
      (this.bookings || []).forEach((b) => {
        const a = fromIso(b.from);
        const z = fromIso(b.to) || a;
        if (!a || z < a) return;
        for (let d = addDays(a, -this.bufferBefore), end = addDays(z, this.bufferAfter); d <= end; d = addDays(d, 1)) {
          const k = toIso(d);
          this.busy[k] = (this.busy[k] || 0) + 1;
        }
      });
    }

    async loadAvailability(url) {
      if (!url) return;
      try {
        const q = `${url}${url.includes('?') ? '&' : '?'}product=${encodeURIComponent(this.handle)}&from=${toIso(this.minDate)}&to=${toIso(this.maxDate)}`;
        const res = await fetch(q, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.serviceBusy = data.busy || {};
        if (data.units) this.units = data.units;
        if (data.bufferBefore != null) this.bufferBefore = data.bufferBefore;
        if (data.bufferAfter != null) this.bufferAfter = data.bufferAfter;
        this.applyBookings();
        (data.blocked || []).forEach((iso) => this.blocked.add(iso));
        this.availabilityChecked = true;
      } catch (e) {
        // The shop stays open if the availability service is down; the order is
        // marked so the booking can be checked by hand.
        this.availabilityChecked = false;
        console.warn('[mini-booking] availability not loaded:', e.message);
      }
      this.render();
    }

    /** Can a rental run from a to b? Counts the days the booth is away before and after. */
    rangeFree(a, b) {
      if (a < this.minDate || b > this.maxDate) return false;
      for (let d = a; d <= b; d = addDays(d, 1)) if (this.blocked.has(toIso(d))) return false;
      for (let d = addDays(a, -this.bufferBefore), end = addDays(b, this.bufferAfter); d <= end; d = addDays(d, 1)) {
        if ((this.busy[toIso(d)] || 0) >= this.units) return false;
      }
      return true;
    }

    /* ---------- events ---------- */
    bind() {
      this.addEventListener('click', (e) => {
        const t = e.target.closest('button');
        if (!t || !this.contains(t)) return undefined;
        if (t.matches('.mb-step-head')) return this.open(t.closest('.mb-step').dataset.step);
        if (t.matches('[data-next]')) return this.next(t.closest('.mb-step').dataset.step);
        if (t.matches('[data-cal-prev]')) return this.moveMonth(-1);
        if (t.matches('[data-cal-next]')) return this.moveMonth(1);
        if (t.matches('.mb-day')) return this.pickDate(t.dataset.iso);
        if (t.matches('[data-trip-pick]')) return this.pickField(t.dataset.tripPick);
        if (t.matches('[data-addon-toggle]')) return this.toggleAddon(t.closest('[data-addon]').dataset.variant);
        if (t.matches('[data-addon-zoom]')) return this.openZoom(t.closest('[data-addon]'));
        if (t.matches('[data-zoom-toggle]')) return this.toggleAddon(this.zoomed);
        if (t.matches('[data-zoom-thumb]')) return this.showZoomImage(+t.dataset.zoomThumb);
        if (t.matches('[data-zoom-more]')) return this.toggleZoomText();
        if (t.matches('[data-remove]')) return this.toggleAddon(t.dataset.remove, false);
        if (t.matches('[data-close-drawer]')) return t.closest('dialog').close();
        if (t.matches('[data-submit]')) return this.submit();
        return undefined;
      });

      this.addEventListener('change', (e) => {
        const t = e.target;
        if (t.name === this.name('delivery')) { this.state.delivery = t.value; this.changed('delivery'); }
        else if (t.name === this.name('logo')) { this.state.logo = t.value; this.changed(); }
        else if (t.matches('[data-occasion]')) { this.state.occasion = t.value; this.changed(); }
        else if (t.name === this.name('customer')) { this.state.customer = t.value; this.changed(); }
        else if (t.name === this.name('payment')) { this.state.payment = t.value; this.changed(); }
        else if (t.name === this.name('invoice-format')) { this.state.invoiceFormat = t.value; this.changed(); }
        else if (t.matches('[data-consent]')) this.setError('summary', '');
      });

      this.addEventListener('input', (e) => {
        const t = e.target;
        if (t.matches('[data-postcode]')) { this.state.postcode = t.value.trim(); this.save(); this.setError('delivery', ''); }
        else if (t.matches('[data-event-name]')) { this.state.eventName = t.value; this.save(); }
        else if (t.matches('[data-company]')) { this.state.company = t.value; this.companyTyped = true; this.save(); }
        else if (t.matches('[data-invoice-email]')) { this.state.invoiceEmail = t.value.trim(); this.save(); this.setError('summary', ''); }
        else if (t.matches('[data-invoice-ref]')) { this.state.invoiceRef = t.value; this.save(); }
        else if (t.matches('[data-orgnr]')) { this.state.orgnr = t.value.replace(/\D/g, '').slice(0, 9); this.save(); this.lookupCompany(); }
      });

      // A click on the dimmed area outside a dialog closes it.
      this.querySelectorAll('dialog').forEach((dlg) => dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); }));

      // While the last day is still to be chosen, the days under the pointer
      // (or the keyboard focus) show what the period and price would be.
      const over = (e) => { const b = e.target.closest('.mb-day'); if (b && !b.disabled) this.preview(b.dataset.iso); };
      this.el.cal.addEventListener('mouseover', over);
      this.el.cal.addEventListener('focusin', over);
      this.el.cal.addEventListener('mouseleave', () => this.preview(null));

      // Arrow keys walk the calendar.
      this.el.cal.addEventListener('keydown', (e) => {
        const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
        const from = e.target.closest('.mb-day');
        if (!step || !from) return;
        e.preventDefault();
        const to = addDays(fromIso(from.dataset.iso), step);
        if (to < new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1) || to > this.maxDate) return;
        if (to.getMonth() !== this.view.getMonth() || to.getFullYear() !== this.view.getFullYear()) {
          this.view = new Date(to.getFullYear(), to.getMonth(), 1);
          this.renderCalendar();
        }
        this.el.cal.querySelector(`[data-iso="${toIso(to)}"]`)?.focus();
      });

      if (this.el.sticky) {
        // The phone bar: with no date yet it leads to the calendar; then it moves the booking on.
        this.el.sticky.querySelector('button')?.addEventListener('click', () => {
          const offset = (document.querySelector('[data-mini-header]')?.getBoundingClientRect().height || 90) + 12;
          const to = (el) => window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
          if (!this.tier()) {
            if (this.state.open !== 'date') this.open('date');
            return to(this.querySelector('.mb-step[data-step="date"]') || this);
          }
          const step = this.querySelector('.mb-step.is-open');
          if (!step || step.getBoundingClientRect().bottom < 0 || step.getBoundingClientRect().top > window.innerHeight) return to(step || this);
          if (this.state.open === 'summary') return this.submit();
          return this.next(this.state.open);
        });
      }
    }

    name(field) { return `mb-${field}-${this.dataset.section}`; }

    /** Something was chosen: later steps that depend on it are checked again. */
    changed(step) {
      // A new date, or an add-on such as Print, can rule out the delivery method that was picked.
      const picked = this.deliveryInput();
      if (picked && !this.deliveryOk(picked).ok) { this.state.delivery = ''; this.undone('delivery'); }
      this.setError(step || this.state.open, '');
      this.save();
      this.render();
    }

    undone(step) { this.state.done = this.state.done.filter((s) => STEPS.indexOf(s) < STEPS.indexOf(step)); }

    applyStateToInputs() {
      const check = (field, value) => {
        const input = [...this.querySelectorAll(`input[name="${this.name(field)}"]`)].find((i) => i.value === value);
        if (input) input.checked = true;
        return !!input;
      };
      if (!check('logo', this.state.logo)) {
        const first = this.querySelector(`input[name="${this.name('logo')}"]`);
        if (first) { first.checked = true; this.state.logo = first.value; }
      }
      if (!check('delivery', this.state.delivery)) this.state.delivery = '';
      check('customer', this.state.customer);
      if (!check('payment', this.state.payment)) this.state.payment = 'now';
      check('invoice-format', this.state.invoiceFormat);
      const set = (sel, v) => { const n = this.querySelector(sel); if (n) n.value = v || ''; };
      set('[data-postcode]', this.state.postcode);
      set('[data-occasion]', this.state.occasion);
      set('[data-event-name]', this.state.eventName);
      set('[data-orgnr]', this.state.orgnr);
      set('[data-company]', this.state.company);
      set('[data-invoice-email]', this.state.invoiceEmail);
      set('[data-invoice-ref]', this.state.invoiceRef);
      const known = new Set([...this.querySelectorAll('[data-addon]')].map((n) => n.dataset.variant));
      this.state.addons = this.state.addons.map(String).filter((id) => known.has(id));
    }

    /* ---------- steps ---------- */
    open(step) {
      const i = STEPS.indexOf(step);
      const reachable = i === 0 || STEPS.slice(0, i).every((s) => this.state.done.includes(s));
      if (!reachable) return;
      this.state.open = step;
      this.save();
      this.render();
      const head = this.querySelector(`.mb-step[data-step="${step}"] .mb-step-head`);
      if (head && head.getBoundingClientRect().top < 80) window.scrollTo({ top: head.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
    }

    next(step) {
      const problem = this.validate(step);
      this.setError(step, problem);
      if (problem) return;
      if (step === 'date') this.picking = false;
      if (!this.state.done.includes(step)) this.state.done.push(step);
      const following = STEPS[STEPS.indexOf(step) + 1];
      if (following) this.open(following);
    }

    validate(step) {
      if (step === 'date') {
        const r = this.range();
        if (!r) return 'Velg en dato i kalenderen.';
        if (!this.tier()) return `Vi leier ut i opptil ${this.maxDays} dager på nett. Ta kontakt for lengre leie.`;
        if (!this.rangeFree(r[0], r[1])) return 'Photoboothen er ikke ledig hele denne perioden. Velg en annen dato eller en kortere periode.';
      }
      if (step === 'delivery') {
        const input = this.deliveryInput();
        if (!input) return 'Velg hvordan du vil motta photoboothen.';
        const ok = this.deliveryOk(input);
        if (!ok.ok) return ok.why;
        if (this.needsPostcode(input)) {
          if (!/^\d{4}$/.test(this.state.postcode)) return 'Skriv inn postnummeret for leveringen (fire siffer).';
          if (input.dataset.oslo === 'true' && !this.inOslo(this.state.postcode)) return 'Denne leveringen tilbys bare i Oslo-området. Velg en annen leveringsmåte.';
          if (input.dataset.outside === 'true' && this.inOslo(this.state.postcode)) return 'Denne leveringen er for adresser utenfor Oslo-området. Velg en annen leveringsmåte.';
        }
      }
      if (step === 'custom' && this.occasionRequired && !this.state.occasion) return 'Velg anledning.';
      if (step === 'summary' && this.state.customer === 'bedrift') {
        if (!/^\d{9}$/.test(this.state.orgnr)) return 'Skriv inn organisasjonsnummeret (ni siffer).';
        if (!this.state.company.trim()) return 'Skriv inn firmanavn.';
        if (this.invoicing() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.invoiceEmail)) return 'Skriv inn e-postadressen fakturaen skal sendes til.';
      }
      if (step === 'summary' && [...this.querySelectorAll('[data-consent]')].some((c) => !c.checked)) return 'Kryss av for at du godtar salgsbetingelsene og personvernerklæringen.';
      return '';
    }

    setError(step, text) {
      const n = this.querySelector(`.mb-step[data-step="${step}"] [data-error]`);
      if (n) n.textContent = text || '';
    }

    /* ---------- step 1: dates and price ---------- */
    moveMonth(n) {
      this.view = new Date(this.view.getFullYear(), this.view.getMonth() + n, 1);
      this.renderCalendar();
    }

    /**
     * Picking dates works like booking a return flight. The highlighted box -
     * first day or last day - is what this click sets; choosing the first day
     * moves on to the last. The weekend rules still apply: a weekend day as the
     * first day means the whole weekend, and the last day cannot be a Friday or
     * Saturday, because the weekend is rented whole (see normalize).
     */
    pickDate(iso) {
      const date = fromIso(iso);
      const s = this.start;
      const [a, b] = this.active === 'end' && s && date >= s ? this.normalize(s, date) : this.normalize(date, date);
      if (daysBetween(a, b) > this.maxDays) return this.setError('date', `Vi leier ut i opptil ${this.maxDays} dager på nett. Ta kontakt for lengre leie.`);
      if (a < this.minDate) return this.setError('date', 'Helgeleie starter på fredag, og det er for sent å få photoboothen frem til denne helgen. Velg en senere dato.');
      if (!this.rangeFree(a, b)) return this.setError('date', 'Photoboothen er ikke ledig alle dagene i denne perioden. Velg en annen dato.');
      this.state.start = toIso(a);
      this.state.end = toIso(b);
      this.active = 'end';
      this.changed('date');
      this.el.cal.querySelector(`[data-iso="${iso}"]`)?.focus();
      return undefined;
    }

    /** A tap on one of the two boxes: the next click sets that day. */
    pickField(which) {
      if (which === 'end' && !this.start) return;
      this.active = which;
      const d = which === 'end' ? fromIso(this.state.end) : this.start;
      if (d) this.view = new Date(d.getFullYear(), d.getMonth(), 1);
      this.setError('date', '');
      this.render();
    }

    /**
     * Can this day be clicked right now? As the last day it cannot come before
     * the first, cannot be a Friday or Saturday (the weekend is rented whole -
     * "bundled"), and every day up to it must be free.
     */
    dayState(date) {
      const s = this.start;
      if (this.active === 'end' && s) {
        if (date < s) return { ok: false, why: 'before' };
        if (toIso(date) !== toIso(s) && (weekday(date) === 4 || weekday(date) === 5)) return { ok: false, why: 'bundled' };
        const [a, b] = this.normalize(s, date);
        return daysBetween(a, b) <= this.maxDays && this.rangeFree(a, b) ? { ok: true } : { ok: false, why: 'taken' };
      }
      const [a, b] = this.normalize(date, date);
      return this.rangeFree(a, b) ? { ok: true } : { ok: false, why: 'taken' };
    }

    preview(iso) {
      const s = this.start;
      const raw = this.active === 'end' && s && iso && fromIso(iso) > s && this.dayState(fromIso(iso)).ok ? fromIso(iso) : null;
      const span = raw ? this.normalize(s, raw) : null;
      this.el.cal.querySelectorAll('.mb-day').forEach((b) => {
        const d = fromIso(b.dataset.iso);
        b.classList.toggle('is-preview', !!span && d > s && d <= span[1]);
      });
      const note = this.el.price && this.el.price.querySelector('[data-preview]');
      if (!note) return;
      note.hidden = !span;
      if (!span) return;
      const n = daysBetween(span[0], span[1]);
      const tier = this.tierFor(span[0], span[1]);
      note.textContent = tier ? `Til og med ${shortDate(span[1])}: ${periodName(tier, n).toLowerCase()} – ${kr(tier.price)}` : `${dayCount(n)} – ta kontakt for så lang leie`;
    }

    /** The two boxes above the calendar. */
    renderTrip() {
      const box = this.querySelector('[data-trip]');
      if (!box) return;
      const r = this.range();
      const field = (which, label, date) => {
        const on = this.active === which;
        const off = which === 'end' && !r;
        return `<button type="button" class="mb-trip-box${on ? ' is-active' : ''}" data-trip-pick="${which}" aria-pressed="${on}"${off ? ' disabled' : ''}>
          <span class="mb-trip-k">${label}</span>
          <span class="mb-trip-v${date ? '' : ' is-empty'}">${date ? shortDate(date) : 'Velg dato'}</span>
        </button>`;
      };
      box.innerHTML = `${field('start', 'Første dag', r && r[0])}<span class="mb-trip-arrow" aria-hidden="true">→</span>${field('end', 'Siste dag', r && r[1])}`;
    }

    renderCalendar() {
      const y = this.view.getFullYear();
      const m = this.view.getMonth();
      const r = this.range();
      const first = new Date(y, m, 1);
      const days = new Date(y, m + 1, 0).getDate();
      const prevOff = y === this.minDate.getFullYear() && m === this.minDate.getMonth();
      const nextOff = y === this.maxDate.getFullYear() && m === this.maxDate.getMonth();
      const picking = this.active === 'end' && r;

      let cells = DAYS_SHORT.map((n) => `<div class="mb-cal-dow" aria-hidden="true">${n}</div>`).join('');
      for (let i = 0; i < weekday(first); i++) cells += '<span></span>';
      for (let day = 1; day <= days; day++) {
        const date = new Date(y, m, day);
        const iso = toIso(date);
        const st = this.dayState(date);
        const taken = !st.ok && st.why === 'taken' && date >= this.minDate && date <= this.maxDate;
        const cls = ['mb-day'];
        if (taken) cls.push('is-taken');
        if (!st.ok && st.why === 'bundled') cls.push('is-bundled');
        if (iso === toIso(this.today)) cls.push('is-today');
        if (r && date >= r[0] && date <= r[1]) {
          cls.push('is-range');
          if (iso === toIso(r[0])) cls.push('is-start');
          if (iso === toIso(r[1])) cls.push('is-end');
        }
        const why = st.why === 'bundled' ? ', helgen leies samlet – velg søndag' : taken ? ', ikke ledig' : '';
        const label = `${DAYS_SHORT[weekday(date)]} ${day}. ${MONTHS[m]} ${y}${why}`;
        const pressed = r && (iso === toIso(r[0]) || iso === toIso(r[1]));
        cells += `<button type="button" class="${cls.join(' ')}" data-iso="${iso}" aria-label="${label}"${st.why === 'bundled' ? ' title="Helgen leies samlet (fredag–søndag) – velg søndag"' : ''}${st.ok ? '' : ' disabled'}${pressed ? ' aria-pressed="true"' : ''}>${day}</button>`;
      }

      this.el.cal.innerHTML = `
        <div class="mb-cal-head">
          <button type="button" class="mb-cal-nav" data-cal-prev aria-label="Forrige måned"${prevOff ? ' disabled' : ''}><svg class="mini-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 5.5L8 12l6.5 6.5"/></svg></button>
          <div class="mb-cal-title" aria-live="polite">${MONTHS[m]} ${y}</div>
          <button type="button" class="mb-cal-nav" data-cal-next aria-label="Neste måned"${nextOff ? ' disabled' : ''}><svg class="mini-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M9.5 5.5L16 12l-6.5 6.5"/></svg></button>
        </div>
        <div class="mb-cal-grid">${cells}</div>
        <div class="mb-cal-legend"><span>Valgt</span><span class="is-na">Ikke ledig</span>${picking ? '<span class="is-bundled">Helgen leies samlet</span>' : ''}</div>`;
    }

    /** The price for the chosen dates, with a word on why a weekend is always whole. */
    renderPrice() {
      const box = this.el.price;
      if (!box) return;
      const r = this.range();
      const tier = this.tier();
      if (!r) {
        box.innerHTML = '<div class="mb-price-now is-empty"><span class="mb-price-dates">Ingen dato valgt ennå</span><span class="mb-price-period">Trykk på første dag i kalenderen, så ser du prisen her.</span></div>';
        return;
      }
      const n = daysBetween(r[0], r[1]);
      const period = !tier ? 'Ta kontakt for så lang leie' : periodName(tier, n);
      const weekend = weekday(r[1]) === 6;
      const tip = this.active === 'end'
        ? 'Trykk på siste dag i kalenderen – eller gå videre hvis du bare trenger photoboothen én dag.'
        : 'Trykk på første dag i kalenderen.';
      box.innerHTML = `<div class="mb-price-now"><span class="mb-price-main"><span class="mb-price-dates">${rangeLabel(r[0], r[1])}</span><span class="mb-price-period">${period}</span></span><strong class="mb-price-amount">${tier ? kr(tier.price) : '–'}</strong></div>
        <p class="mb-price-tip mb-price-tip--live" data-preview hidden></p>
        ${weekend ? '<p class="mb-price-tip">Du får photoboothen før fredag og sender den tilbake første hverdag etter arrangementet. Derfor gjelder leien alltid hele helgen.</p>' : ''}
        <p class="mb-price-tip">${tip}</p>`;
    }

    /* ---------- step 2: delivery ---------- */
    deliveryInput() {
      return [...this.querySelectorAll(`input[name="${this.name('delivery')}"]`)].find((i) => i.value === this.state.delivery) || null;
    }

    /** Is this delivery method possible for the chosen date? */
    deliveryOk(input) {
      if (input.dataset.post === 'true') {
        const blocker = this.chosenAddons().find((a) => a.noPost);
        if (blocker) return { ok: false, why: `Du har valgt ${blocker.title.toLowerCase()}, og da kan photoboothen ikke sendes med Posten. Velg en av de andre leveringsmåtene.` };
      }
      const r = this.range();
      const lead = parseInt(input.dataset.lead, 10) || 0;
      if (!r || lead <= 0) return { ok: true };
      if (r[0] >= this.earliestFor(input)) return { ok: true };
      if (input.dataset.business === 'true') {
        const cutoff = parseInt(input.dataset.cutoff, 10) || 0;
        const friday = weekday(r[0]) === 4 && daysBetween(r[0], r[1]) >= 3 ? ' Helgeleie starter på fredag.' : '';
        return { ok: false, why: `For sent til datoen du har valgt: må bestilles ${cutoff ? `innen kl. ${pad(cutoff)}.00 ` : ''}${lead === 1 ? '1 arbeidsdag' : `${lead} arbeidsdager`} før leiestart (helg og helligdager teller ikke).${friday}` };
      }
      return { ok: false, why: lead === 1 ? 'Må bestilles minst 1 dag før.' : `Må bestilles minst ${lead} dager før – for kort tid til datoen du har valgt.` };
    }

    /**
     * The first rental day a delivery method can still reach. "Minst dager før"
     * counts back from that day. With Posten's working days only Monday to
     * Friday outside holidays count, and the day the order comes in counts
     * only if it is a working day and the time limit has not passed. Ekspress
     * (2 days, by 12.00): ordered Monday morning, it can be there for a rental
     * from Wednesday; ordered Thursday afternoon, from Tuesday.
     */
    earliestFor(input) {
      const lead = parseInt(input.dataset.lead, 10) || 0;
      const cutoff = parseInt(input.dataset.cutoff, 10) || 0;
      const now = cutoff || input.dataset.business === 'true' ? osloNow() : { date: this.today, hour: 0 };
      if (input.dataset.business !== 'true') return addDays(now.date, lead + (cutoff && now.hour >= cutoff ? 1 : 0));
      let day = now.date;
      if (!isPostDay(day) || (cutoff && now.hour >= cutoff)) day = nextPostDay(day);
      for (let n = 1; n < lead; n += 1) day = nextPostDay(day);
      return addDays(day, 1);
    }

    /** Budbil in Oslo and Ekspress outside it ask where the booth is going. */
    needsPostcode(input) { return input.dataset.oslo === 'true' || input.dataset.outside === 'true'; }

    /** The add-on that changes this delivery method (Print, for Budbil) is chosen. */
    withAddon(input) { return !!input.dataset.freeWith && this.state.addons.includes(String(input.dataset.freeWith)); }

    /** What a delivery method costs now. One can be free with an add-on: Budbil with Print. */
    deliveryCost(input) {
      const price = +input.dataset.price || 0;
      if (price && this.withAddon(input)) return { price: 0, variant: null, note: `Gratis med ${input.dataset.freeWithTitle}` };
      return { price, variant: price ? input.dataset.variant || null : null, note: '' };
    }

    renderDelivery() {
      this.querySelectorAll(`input[name="${this.name('delivery')}"]`).forEach((input) => {
        const ok = this.deliveryOk(input);
        input.disabled = !ok.ok;
        if (!ok.ok && input.checked) { input.checked = false; this.state.delivery = ''; }
        const opt = input.closest('.mb-opt');
        const warn = opt.querySelector('[data-warn]');
        if (warn) { warn.hidden = ok.ok; warn.textContent = ok.ok ? '' : ok.why; }
        const price = opt.querySelector('[data-opt-price]');
        if (price) { const cost = this.deliveryCost(input); price.textContent = cost.price ? `+ ${kr(cost.price)}` : 'Gratis'; }
        // Budbil says something else with Print: we fetch the booth, so no Post i Butikk.
        const on = this.withAddon(input);
        opt.querySelectorAll('[data-with-addon]').forEach((n) => { n.hidden = !on; });
        opt.querySelectorAll('[data-without-addon]').forEach((n) => { n.hidden = on; });
      });
      const picked = this.deliveryInput();
      const pc = this.querySelector('[data-postcode-row]');
      if (pc) pc.hidden = !(picked && this.needsPostcode(picked));
    }

    /* ---------- step 3: add-ons ---------- */
    addonCard(variant) { return [...this.querySelectorAll('[data-addon]')].find((n) => n.dataset.variant === String(variant)) || null; }

    /** Add or take away an add-on; pass false as the second argument to only take away. */
    toggleAddon(variant, allowAdd = true) {
      const id = String(variant);
      const on = this.state.addons.includes(id);
      if (!on && !allowAdd) return;
      this.state.addons = on ? this.state.addons.filter((v) => v !== id) : this.state.addons.concat(id);
      this.changed();
    }

    chosenAddons(group) {
      const scope = group || this;
      return [...scope.querySelectorAll('[data-addon]')].filter((n) => this.state.addons.includes(n.dataset.variant))
        .map((n) => ({ variant: n.dataset.variant, title: n.dataset.title, price: +n.dataset.price, image: n.dataset.thumb || '', noPost: n.hasAttribute('data-no-post'), group: n.closest('[data-addon-group]')?.dataset.label || '' }));
    }

    renderAddons() {
      this.querySelectorAll('[data-addon]').forEach((card) => {
        const on = this.state.addons.includes(card.dataset.variant);
        card.classList.toggle('is-on', on);
        const btn = card.querySelector('[data-addon-toggle]');
        if (btn) { btn.setAttribute('aria-pressed', on); btn.textContent = on ? '✓ Lagt til' : '+ Legg til'; btn.title = on ? 'Trykk for å fjerne' : ''; }
      });
      this.querySelectorAll('[data-addon-group]').forEach((group) => {
        const n = this.chosenAddons(group).length;
        const count = group.querySelector('[data-count]');
        if (count) { count.hidden = !n; count.textContent = n ? `${n} valgt` : ''; }
      });
      if (this.el.zoom && this.el.zoom.open) this.fillZoom();
    }

    /* ---------- the "Les mer" window for one add-on ---------- */
    openZoom(card) {
      if (!this.el.zoom || !card) return;
      this.zoomed = card.dataset.variant;
      this.fillZoom(true);
      this.el.zoom.showModal();
    }

    /** Fill the window. With fresh = true the pictures and texts are rebuilt; otherwise only what a click can change. */
    fillZoom(fresh) {
      const card = this.addonCard(this.zoomed);
      const z = this.el.zoom;
      if (!card || !z) return;

      if (fresh) {
        z.querySelector('[data-zoom-name]').textContent = card.dataset.title;
        z.querySelector('[data-zoom-price]').textContent = `+ ${kr(+card.dataset.price)}`;

        let media = [];
        try { media = JSON.parse(card.querySelector('[data-addon-media]')?.textContent || '[]'); } catch (e) { media = []; }
        this.zoomMedia = media;
        z.querySelector('[data-zoom-thumbs]').innerHTML = media.length > 1
          ? media.map((m, i) => `<button type="button" class="mb-modal-thumb" data-zoom-thumb="${i}" aria-label="Bilde ${i + 1}"><img src="${esc(m.thumb)}" alt="" loading="lazy"></button>`).join('')
          : '';
        z.querySelector('.mb-modal-gallery').hidden = !media.length;
        this.showZoomImage(0);

        // The product's description: its first list becomes the ticked points,
        // anything else waits behind "Les hele beskrivelsen".
        const points = z.querySelector('[data-zoom-points]');
        const text = z.querySelector('[data-zoom-text]');
        const wrap = z.querySelector('[data-zoom-more-wrap]');
        const more = z.querySelector('[data-zoom-more]');
        points.innerHTML = '';
        text.innerHTML = '';
        const tpl = card.querySelector('[data-addon-desc]');
        if (tpl) {
          const frag = tpl.content.cloneNode(true);
          const list = frag.querySelector('ul, ol');
          if (list) { points.appendChild(list); text.appendChild(frag); } else points.appendChild(frag);
        }
        wrap.hidden = !text.textContent.trim();
        text.hidden = true;
        more.setAttribute('aria-expanded', 'false');
        more.textContent = 'Les hele beskrivelsen';
      }

      const on = this.state.addons.includes(card.dataset.variant);
      const btn = z.querySelector('[data-zoom-toggle]');
      btn.textContent = on ? 'Fjern fra bestillingen' : '+ Legg til';
      btn.classList.toggle('mini-btn--ghost', on);
      const chosen = this.chosenAddons();
      z.querySelector('[data-zoom-status]').textContent = chosen.length
        ? `${chosen.length} tilvalg valgt · + ${kr(chosen.reduce((n, a) => n + a.price, 0))}`
        : 'Ingen tilvalg valgt ennå';
    }

    showZoomImage(i) {
      const z = this.el.zoom;
      const m = (this.zoomMedia || [])[i];
      if (!z || !m) return;
      const img = z.querySelector('[data-zoom-img]');
      if (img.getAttribute('src') !== m.src) img.src = m.src;
      img.alt = m.alt || '';
      z.querySelectorAll('[data-zoom-thumb]').forEach((t) => t.setAttribute('aria-current', +t.dataset.zoomThumb === i));
    }

    toggleZoomText() {
      const z = this.el.zoom;
      const text = z.querySelector('[data-zoom-text]');
      const more = z.querySelector('[data-zoom-more]');
      text.hidden = !text.hidden;
      more.setAttribute('aria-expanded', !text.hidden);
      more.textContent = text.hidden ? 'Les hele beskrivelsen' : 'Skjul beskrivelsen';
    }

    /* ---------- totals and summary ---------- */
    /** The invoice option is chosen, and the customer is a business. */
    invoicing() {
      return this.state.customer === 'bedrift' && this.state.payment === 'invoice' && !!this.querySelector(`input[name="${this.name('payment')}"][value="invoice"]`);
    }

    lines() {
      const out = [];
      const r = this.range();
      const tier = this.tier();
      if (r && tier) out.push({ key: `${this.productTitle} – ${periodName(tier, daysBetween(r[0], r[1])).toLowerCase()}`, note: rangeLabel(r[0], r[1]), price: tier.price, variant: tier.id, main: true, image: this.dataset.thumb || '', icon: 'camera' });

      const delivery = this.deliveryInput();
      if (delivery) {
        const cost = this.deliveryCost(delivery);
        const where = this.needsPostcode(delivery) && this.state.postcode ? `Postnummer ${this.state.postcode}` : '';
        out.push({ key: `Levering: ${delivery.dataset.label}`, note: [where, cost.note].filter(Boolean).join(' · '), price: cost.price, variant: cost.variant, free: 'Gratis', icon: 'truck' });
      }
      const logo = this.querySelector(`input[name="${this.name('logo')}"]:checked`);
      if (logo) out.push({ key: `Logo/design: ${logo.dataset.label}`, price: +logo.dataset.price || 0, variant: logo.dataset.variant || null, free: 'Inkludert', icon: 'palette' });
      // "Rekvisitter – bryllup" already names its group; only a bare name gets the group in front.
      this.chosenAddons().forEach((a) => {
        const named = a.group && a.title.toLowerCase().startsWith(a.group.toLowerCase());
        out.push({ key: a.group && !named ? `${a.group}: ${a.title}` : a.title, price: a.price, variant: a.variant, image: a.image, removable: true });
      });
      if (this.invoicing()) {
        const fee = this.querySelector(`input[name="${this.name('payment')}"][value="invoice"]`);
        out.push({ key: 'Fakturagebyr', note: `Faktura som ${this.state.invoiceFormat}, 7 dagers betalingsfrist`, price: +fee.dataset.price || 0, variant: fee.dataset.variant || null, icon: 'invoice' });
      }
      return out;
    }

    total() { return this.lines().reduce((n, l) => n + l.price, 0); }

    renderSummary() {
      const lines = this.lines();
      if (this.el.sum) {
        this.el.sum.innerHTML = lines.map((l) => `
          <li>
            <span class="mb-sum-img${l.image ? '' : ' mb-sum-img--icon'}">${l.image ? `<img src="${esc(l.image)}" alt="" loading="lazy">` : (ICONS[l.icon] || '')}</span>
            <span class="mb-sum-k">${esc(l.key)}${l.note ? `<small>${esc(l.note)}</small>` : ''}${l.removable ? `<button type="button" class="mb-sum-x" data-remove="${esc(l.variant)}" aria-label="Fjern ${esc(l.key)}">Fjern</button>` : ''}</span>
            <span class="mb-sum-v">${l.price ? kr(l.price) : esc(l.free || kr(0))}</span>
          </li>`).join('');
      }
      const tier = this.tier();
      const total = this.total();
      const text = tier ? kr(total) : (this.tiers.length ? `Fra ${kr(Math.min(...this.tiers.map((t) => t.price)))}` : '');
      this.querySelectorAll('[data-total]').forEach((n) => { n.textContent = text; });
      if (this.el.sticky) {
        this.el.sticky.querySelector('[data-total]').textContent = text;
        const k = this.el.sticky.querySelector('[data-sticky-k]');
        if (k) k.hidden = !tier;
        const btn = this.el.sticky.querySelector('button');
        btn.innerHTML = !tier ? 'Se ledige datoer <span class="mini-arrow" aria-hidden="true">&rarr;</span>' : this.state.open === 'summary' ? 'Til kassen' : 'Neste';
        this.el.sticky.classList.add('is-on');
      }
      const org = this.querySelector('[data-org-fields]');
      if (org) org.hidden = this.state.customer !== 'bedrift';
      const inv = this.querySelector('[data-invoice-fields]');
      if (inv) inv.hidden = !this.invoicing();
      // The note above the button says how paying works for the chosen way.
      const noteInv = this.querySelector('[data-note-invoice]');
      if (noteInv) {
        noteInv.hidden = !this.invoicing();
        const notePay = this.querySelector('[data-note-pay]');
        if (notePay) notePay.hidden = this.invoicing();
      }
    }

    renderHeads() {
      const r = this.range();
      const tier = this.tier();
      const delivery = this.deliveryInput();
      const logo = this.querySelector(`input[name="${this.name('logo')}"]:checked`);
      const extras = this.chosenAddons().length;
      const summary = {
        date: r && tier ? `${rangeLabel(r[0], r[1])} · ${{ weekend: 'helg', longweekend: 'langhelg' }[tier.kind] || dayCount(daysBetween(r[0], r[1]))}` : '',
        delivery: delivery ? delivery.dataset.label : '',
        custom: [logo ? logo.dataset.short || logo.dataset.label : '', extras ? `${extras} tilvalg` : ''].filter(Boolean).join(' · '),
        summary: '',
      };
      this.querySelectorAll('.mb-step').forEach((li) => {
        const step = li.dataset.step;
        const isOpen = this.state.open === step;
        const done = this.state.done.includes(step);
        const i = STEPS.indexOf(step);
        const reachable = i === 0 || STEPS.slice(0, i).every((s) => this.state.done.includes(s));
        li.classList.toggle('is-open', isOpen);
        li.classList.toggle('is-done', done && !isOpen);
        const head = li.querySelector('.mb-step-head');
        head.setAttribute('aria-expanded', isOpen);
        head.disabled = !reachable;
        li.querySelector('[data-summary]').textContent = summary[step] || '';
      });
    }

    render() {
      this.renderTrip();
      this.renderCalendar();
      this.renderPrice();
      this.renderDelivery();
      this.renderAddons();
      this.renderSummary();
      this.renderHeads();
    }

    /* ---------- company lookup (Brønnøysundregistrene, open data) ---------- */
    async lookupCompany() {
      const orgnr = this.state.orgnr;
      const note = this.querySelector('[data-org-note]');
      if (note) note.textContent = '';
      if (!/^\d{9}$/.test(orgnr) || this.companyTyped) return;
      try {
        const res = await fetch(`https://data.brreg.no/enhetsregisteret/api/enheter/${orgnr}`, { headers: { Accept: 'application/json' } });
        if (orgnr !== this.state.orgnr) return;
        if (!res.ok) { if (note) note.textContent = 'Fant ikke organisasjonsnummeret. Skriv inn firmanavnet selv.'; return; }
        const data = await res.json();
        if (data.navn && !this.companyTyped) {
          this.state.company = data.navn;
          const field = this.querySelector('[data-company]');
          if (field) field.value = data.navn;
          this.save();
        }
      } catch (e) { /* no network or blocked: the customer types the name */ }
    }

    /* ---------- hand-over to the cart ---------- */
    async submit() {
      for (const step of STEPS) {
        const problem = this.validate(step);
        if (problem) { this.open(step); this.setError(step, problem); return; }
      }
      const btn = this.el.submit;
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Et øyeblikk …';

      try {
        const r = this.range();
        const tier = this.tier();
        const lines = this.lines();
        const booking = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const delivery = this.deliveryInput();
        const logo = this.querySelector(`input[name="${this.name('logo')}"]:checked`);
        const when = rangeLabel(r[0], r[1]);

        // Shown on the order and in the confirmation e-mail. Names that start
        // with an underscore are for the shop's own systems and stay hidden.
        // The period itself is the variant's name; the number of days is added
        // only where the variant covers a span ("3-4 dager").
        const main = { Dato: when };
        if (tier.kind === 'days' && tier.min !== tier.max) main['Antall dager'] = String(daysBetween(r[0], r[1]));
        if (delivery) main.Levering = delivery.dataset.label + (this.needsPostcode(delivery) && this.state.postcode ? ` (${this.state.postcode})` : '');
        if (logo) main['Logo/design'] = logo.dataset.label;
        if (this.state.occasion) main.Anledning = this.state.occasion;
        if (this.state.eventName.trim()) main.Arrangement = this.state.eventName.trim();
        Object.assign(main, { _booking: booking, _start: toIso(r[0]), _end: toIso(r[1]) });
        if (this.availabilityChecked === false) main._availability = 'ikke sjekket';

        // One package: every add-on goes in as a line nested under the photobooth
        // (parent_id). Shopify then keeps them together by itself - in the cart,
        // the checkout and the order - and takes the add-ons out when the
        // photobooth is removed.
        const parent = +tier.id;
        const items = lines.filter((l) => l.variant).map((l) => l.main
          ? { id: +l.variant, quantity: 1, properties: main }
          : { id: +l.variant, quantity: 1, parent_id: parent, properties: { _booking: booking, _addon: '1' } })
          .sort((a, b) => (a.parent_id ? 1 : 0) - (b.parent_id ? 1 : 0));

        const post = async (path, body) => {
          const res = await fetch(`${root()}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
          if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || `HTTP ${res.status}`);
          return res.json();
        };

        // One booking per order: an earlier attempt left in the cart is replaced.
        // Photobooth lines first - their nested add-ons leave with them - then
        // any add-on that was somehow left on its own.
        const getCart = async () => (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
        for (const item of (await getCart()).items) {
          if (item.properties && item.properties._booking && !item.properties._addon) await post('cart/change.js', { id: item.key, quantity: 0 });
        }
        for (const item of (await getCart()).items) {
          if (item.properties && item.properties._addon) await post('cart/change.js', { id: item.key, quantity: 0 });
        }

        await post('cart/add.js', { items });
        const company = this.state.customer === 'bedrift';
        const invoice = this.invoicing();
        await post('cart/update.js', { attributes: {
          Kundetype: company ? 'Bedrift' : 'Privatperson',
          Organisasjonsnummer: company ? this.state.orgnr : '',
          Firmanavn: company ? this.state.company.trim() : '',
          Betaling: invoice ? 'Faktura' : 'Betal nå',
          Fakturaformat: invoice ? this.state.invoiceFormat : '',
          'Faktura-e-post': invoice ? this.state.invoiceEmail : '',
          Fakturareferanse: invoice ? this.state.invoiceRef.trim() : '',
          // When the customer ticked both boxes - the record that they accepted.
          'Godtatt vilkår og personvern': new Date().toLocaleString('nb-NO', { timeZone: 'Europe/Oslo' }),
        } });

        window.location.href = `${root()}checkout`;
      } catch (e) {
        btn.disabled = false;
        btn.textContent = label;
        this.setError('summary', `Noe gikk galt da bestillingen skulle sendes videre (${e.message}). Prøv igjen.`);
      }
    }
  }

  customElements.define('mini-booking', MiniBooking);
})();
